<template>
  <a-config-provider :theme="theme">
    <router-view />
  </a-config-provider>
</template>

<script setup lang="ts">
import { ConfigProvider as AConfigProvider } from 'ant-design-vue'

const theme = {
  token: {
    colorPrimary: '#1890ff',
    colorBorder: '#f0f0f0',
    borderRadius: 8,
    fontSize: 14,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    padding: 24,
    margin: 16,
    // Tag-specific styling for professional appearance
    tagFontSize: 13,
    tagBorderRadius: 6,
    tagPaddingInline: 10,
    tagPaddingBlock: 6,
  },
}
// Root component - Router handles the layout
</script>

<style>
/* Global styles */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial,
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

#app {
  width: 100%;
  min-height: 100vh;
}

/* Professional tag styling */
.ant-tag {
  font-size: 13px;
  line-height: 1.5;
  padding: 6px 10px;
  border-radius: 6px;
  min-width: 60px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
}

/* Tag hover effects */
.ant-tag:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
}

/* Close button in tags */
.ant-tag .anticon-close {
  font-size: 12px;
  margin-left: 6px;
  transition: all 0.2s ease;
}

.ant-tag .anticon-close:hover {
  transform: scale(1.1);
  color: #ff4d4f;
}

/* Responsive tag sizing for mobile */
@media (max-width: 768px) {
  .ant-tag {
    font-size: 12px;
    padding: 5px 8px;
    min-width: 50px;
  }
}

/* Select dropdown styling - ensure full width */
.ant-select {
  width: 100% !important;
}

/* Select selector (the input box) */
.ant-select-selector {
  min-height: 42px !important;
  padding: 4px 11px !important;
  border-radius: 8px !important;
  border: 1px solid #d9d9d9 !important;
  font-size: 14px !important;
  transition: all 0.2s ease !important;
}

.ant-select-selector:hover {
  border-color: #4096ff !important;
}

.ant-select-focused .ant-select-selector {
  border-color: #4096ff !important;
  box-shadow: 0 0 0 2px rgba(64, 150, 255, 0.1) !important;
}

/* Tags inside select */
.ant-select-selection-item {
  font-size: 13px !important;
  font-weight: 500 !important;
  padding: 4px 8px !important;
  border-radius: 6px !important;
  max-width: 200px !important;
}

/* Select tag removal */
.ant-select-selection-item-remove {
  font-size: 12px !important;
  margin-left: 4px !important;
}

.ant-select-selection-item-remove:hover {
  color: #ff4d4f !important;
}

/* Select placeholder */
.ant-select-selection-placeholder {
  font-size: 14px !important;
  color: rgba(0, 0, 0, 0.25) !important;
}

/* Accessibility: Focus visible for keyboard navigation */
.ant-select:focus-visible,
.ant-select-selector:focus-visible {
  outline: 2px solid #4096ff !important;
  outline-offset: 2px !important;
}

/* Disabled state */
.ant-select-disabled .ant-select-selector {
  background: #f5f5f5 !important;
  cursor: not-allowed !important;
  opacity: 0.6;
}

/* Dropdown menu styling */
.ant-select-dropdown {
  border-radius: 8px !important;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12) !important;
  padding: 0 !important;
}

.ant-select-dropdown .ant-select-item {
  padding: 10px 12px !important;
  font-size: 14px !important;
  border-radius: 0 !important;
}

.ant-select-item-option-selected {
  background: #e6f4ff !important;
  font-weight: 500 !important;
}

.ant-select-item-option-active {
  background: #f5f5f5 !important;
}

/* Loading state */
.ant-select-loading .ant-select-selector {
  cursor: wait !important;
}

/* Arrow icon */
.ant-select-arrow {
  color: rgba(0, 0, 0, 0.45) !important;
  font-size: 12px !important;
}

/* Clear button */
.ant-select-clear {
  background: #fff !important;
  color: rgba(0, 0, 0, 0.45) !important;
}

.ant-select-clear:hover {
  color: rgba(0, 0, 0, 0.85) !important;
}

/* Buttons - ensure consistency */
.ant-btn {
  border-radius: 8px !important;
  font-weight: 500 !important;
  cursor: pointer !important;
  transition: all 0.2s ease !important;
}

.ant-btn:hover {
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.ant-btn:active {
  transform: translateY(0) !important;
}

.ant-btn-primary {
  background: #1890ff !important;
  border-color: #1890ff !important;
}

.ant-btn-primary:hover {
  background: #40a9ff !important;
  border-color: #40a9ff !important;
}

.ant-btn-primary:focus {
  background: #40a9ff !important;
  border-color: #40a9ff !important;
}

/* Card styling */
.ant-card {
  border-radius: 8px !important;
  border: 1px solid #f0f0f0 !important;
}

.ant-card-head {
  border-bottom: 1px solid #f0f0f0 !important;
  padding: 16px 24px !important;
}

.ant-card-head-title {
  font-weight: 600 !important;
  font-size: 16px !important;
}

.ant-card-body {
  padding: 24px !important;
}

/* Checkbox improvements */
.ant-checkbox-wrapper {
  font-size: 14px !important;
  cursor: pointer !important;
}

.ant-checkbox-inner {
  width: 18px !important;
  height: 18px !important;
  border-radius: 4px !important;
}

.ant-checkbox-checked .ant-checkbox-inner {
  background: #1890ff !important;
  border-color: #1890ff !important;
}
</style>
